import ApiBase from '../base/apiBase';
import {
  IJobData,
  IJobCities,
  IJobParams,
  IJob,
  JobCitiesResultsType
} from 'core/interface/job';
import { IApplicationResult, IJobDTO } from 'core/interface/jobDTO';
import { AppendLocations, SaveLocations } from '../../redux/slices/location';
import { store } from '../../redux/store';
import {
  SaveEditJobs,
  SaveJobProfileCandidate,
  SaveJobs,
  SaveSingleJobs
} from '../../redux/slices/jobs';
import { SaveApplications } from '../../redux/slices/apllications';
import {
  SaveAssessmentByGroup,
  SaveAssessmentReport,
  SaveAssessmentValues,
  ClearAssessment
} from '../../redux/slices/assessment';
import { prepareUpdatedData } from 'helpers/stateChanges';
import { enqueueSnackbar } from 'notistack';

export class JobService {
  private static apiBase = new ApiBase<IJobData>();
  private static activeJobId: string | number | null = null;
  private static activeJobProfileCandidatesId: string | null = null;

  public static async getJobs(
    params?: IJobParams & { limit?: number; offset?: number }
  ): Promise<IJobData> {
    let queryParams = '';
    let isFirstParam = true;
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        // Skip empty / invalid filter values (e.g. industries=undefined from String(undefined))
        if (
          value === undefined ||
          value === null ||
          value === '' ||
          value === 'undefined' ||
          value === 'null'
        ) {
          continue;
        }
        queryParams += isFirstParam
          ? `/?${key}=${value}`
          : `&${key}=${value}`;
        isFirstParam = false;
      }
    }
    const { dispatch } = store;
    const res = await this.apiBase.getAsync(
      `/jobs${queryParams}${params ? '' : '/'}`
    );
    dispatch(SaveJobs(res));
    return res;
  }

  public static async getLocations(
    params: object,
    append = false
  ): Promise<IJobCities | null> {
    const { dispatch } = store;
    try {
      // Determine the base URL and ensure params has a limit
      const baseParams = { limit: 10, ...params };

      const res = await this.apiBase.getAsync(
        'https://nora.recrout.com/api/cities/',
        baseParams
      );

      if (!res?.results) {
        console.warn('No results returned from cities API');
        return null;
      }

      // Only deduplicate if we're appending to existing results
      if (append) {
        const cities = store.getState().locations?.cities;
        const existingIds = new Set(
          (Array.isArray(cities) ? cities : []).map(
            (city: JobCitiesResultsType) => city.id
          )
        );

        // Filter out cities that already exist in the store
        const newCities = res.results.filter(
          (city: JobCitiesResultsType) => !existingIds.has(city.id)
        );

        if (newCities.length > 0) {
          dispatch(AppendLocations(newCities));
        }
      } else {
        // For initial load, just save all results
        dispatch(SaveLocations(res.results));
      }

      return res;
    } catch (error) {
      console.error('Error fetching locations:', error);
      // Optionally show error to user
      enqueueSnackbar?.('Error fetching locations. Please try again.', {
        variant: 'error'
      });
      return null;
    }
  }

  public static async getJobByIdOrSlug(
    idOrSlug: string | number,
    isPublic: boolean = false
  ): Promise<IJob | null> {
    const { dispatch } = store;
    const url = isPublic
      ? `/jobs/${idOrSlug}/?public=true`
      : `/jobs/${idOrSlug}/`;
    if (!isPublic) {
      this.activeJobId = idOrSlug;
    }
    try {
      const res = await this.apiBase.getAsync(url);
      if (isPublic || this.activeJobId === idOrSlug) {
        dispatch(SaveSingleJobs(res));
      }
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async getJobProfileCandidatesById(
    id: string,
    params: any,
    body: any = {}
  ): Promise<any | void> {
    try {
      const { dispatch } = store;
      this.activeJobProfileCandidatesId = id;

      const res = await this.apiBase.postAsync(
        `/jobs/${id}/find_candidates/`,
        body ?? {},
        params
      );

      if (this.activeJobProfileCandidatesId === id) {
        dispatch(SaveJobProfileCandidate(res));
      }
      return res;
    } catch (error) {
      console.error('Error fetching:', error);
    }
  }

  public static async deleteJobByIdOrSlug(
    idOrSlug: string | number
  ): Promise<boolean | null> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.deleteAsync(`/jobs/${idOrSlug}/`);
      const updatedState = prepareUpdatedData(
        'delete',
        'jobs',
        {
          id: idOrSlug
        },
        'jobs'
      );
      dispatch(SaveJobs(updatedState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async updateJobByIdOrSlug(
    idOrSlug: string | number,
    body: IJob
  ): Promise<IJob> {
    const { dispatch } = store;
    const res = await this.apiBase.updateAsync(`/jobs/${idOrSlug}/`, body);
    dispatch(SaveSingleJobs(res));
    return res;
  }

  public static async getassesments(user_id: number): Promise<void> {
    const { dispatch } = store;

    if (!user_id) {
      dispatch(ClearAssessment());
      return;
    }

    try {
      const res = await this.apiBase.getAsync(
        `/assessment/assessments/?user_id=${user_id}`,
        { timeout: 15000 }
      );

      if (!res) {
        dispatch(ClearAssessment());
        return;
      }

      dispatch(SaveAssessmentValues(res));
      return res;
    } catch (error) {
      console.error('Error fetching assessments:', error);
      dispatch(ClearAssessment());
      throw error;
    }
  }

  public static async getAssessmentByGroup(): Promise<void> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.getAsync(
        `/assessment/competencies_by_group/`
      );
      dispatch(SaveAssessmentByGroup(res));
      return res;
    } catch (error) {
      console.error('Error fetching assessments:', error);
    }
  }

  public static async postAssessments(user_id: number): Promise<any> {
    return this.apiBase.postAsync('/assessment/assessments/', {
      user: user_id
    });
  }

  public static async getAssesmentsResult(user_id: number) {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.getAsync(
        `/assessment/assessments/?user_id=${user_id}`,
        { timeout: 15000 }
      );
      dispatch(SaveAssessmentReport(res));
    } catch (error) {
      console.error('Error fetching assessments report:', error);
    }
  }

  public static async saveCultureProfile(): Promise<void> {
    const body = {
      title: 'Sub Zero',
      anonymous: false,
      company: 1,
      competencies: [
        { competency: 4, min_score: 2 },
        { competency: 18, min_score: 3 },
        { competency: 22, min_score: 5 },
        { competency: 33, min_score: 5 },
        { competency: 45, min_score: 3 }
      ],
      is_culture: true
    };
    return this.apiBase.postAsync(`/match/profiles/?culture=true`, body);
  }

  public static async getApplications(
    id?: number,
    searchQuery?: string,
    limit?: number,
    offset?: number,
    ordering?: string
  ): Promise<IJobDTO<IApplicationResult[]> | null> {
    const { dispatch } = store;
    try {
      const queryParams = new URLSearchParams();
      if (id) queryParams.append('jobID', id.toString());
      if (searchQuery) queryParams.append('q', searchQuery);
      if (limit !== undefined) queryParams.append('limit', limit.toString());
      if (offset !== undefined) queryParams.append('offset', offset.toString());
      if (ordering) queryParams.append('ordering', ordering);
      const res = await this.apiBase.getAsync(
        `/jobs/applications/?${queryParams.toString()}`
      );
      dispatch(SaveApplications(res));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async applyForAJob(job: number | any): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.postAsync(`/jobs/applications/`, { job });
      dispatch(SaveApplications(res));
      return res;
    } catch (error) {
      return null;
    }
  }

  public static async manageJob(id: number, data: any | any): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.updateAsync(
        `/jobs/applications/${id}/manage/`,
        data
      );
      const isDeleted = data?.status === 5;
      const updatedState = prepareUpdatedData(
        isDeleted ? 'delete' : 'update',
        'applications',
        isDeleted ? { id } : { id, ...res },
        'applications'
      );
      if (data?.status === 4) {
        enqueueSnackbar('Request Cancelled', {
          variant: 'success'
        });
      }
      if (isDeleted) {
        enqueueSnackbar('Application deleted', {
          variant: 'success'
        });
      }
      dispatch(SaveApplications(updatedState));
      return res;
    } catch (error) {
      return null;
    }
  }

  public static async rejectApplication(id: number): Promise<boolean | null> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.updateAsync(
        `/jobs/applications/${id}/cancel/`,
        {}
      );
      const updatedState = prepareUpdatedData(
        'delete',
        'applications',
        {
          id
        },
        'applications'
      );
      dispatch(SaveApplications(updatedState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async saveJob(body: unknown): Promise<IJobData | void> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.postAsync(`/jobs/`, body);
      const updatedState = prepareUpdatedData('create', 'jobs', res, 'jobs');
      dispatch(SaveJobs(updatedState));
      return res;
    } catch (error: any) {
      console.error('Error creating job:', error);

      // The error might be the direct response data from the server
      // or it might be nested in the error object
      let errorMessage = '';

      // Direct message in the error object (which could be the response data)
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          errorMessage = error.message;
        }
      }

      // Check for nested message in error.response.data
      if (!errorMessage && error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // If we found a subscription limit message, show it
      if (errorMessage && errorMessage.includes('subscription limit')) {
        enqueueSnackbar('You have reached your subscription limit.', {
          variant: 'error'
        });
      }
    }
  }

  public static async saveJobEdit(
    id: number,
    body: unknown
  ): Promise<IJob | null> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.updateAsync(`/jobs/${id}/`, body);
      const updatedState = prepareUpdatedData('update', 'jobs', res, 'jobs');
      dispatch(SaveEditJobs(updatedState));
      if (this.activeJobId != null && String(this.activeJobId) === String(id)) {
        dispatch(SaveSingleJobs(res));
      }
      return res;
    } catch (error: any) {
      console.error('Error saving job edit:', error);

      // The error might be the direct response data from the server
      // or it might be nested in the error object
      let errorMessage = '';

      // Direct message in the error object (which could be the response data)
      if (typeof error === 'object' && error !== null) {
        if ('message' in error) {
          errorMessage = error.message;
        }
      }

      // Check for nested message in error.response.data
      if (!errorMessage && error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }

      // If we found a subscription limit message, show it
      if (errorMessage && errorMessage.includes('subscription limit')) {
        enqueueSnackbar('You have reached your subscription limit.', {
          variant: 'error'
        });
      }
      return null;
    }
  }

  public static async acceptApplication(id: number): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.getAsync(
        `/jobs/applications/${id}/accept/`,
        {}
      );
      const updatedState = prepareUpdatedData(
        'update',
        'applications',
        { id, ...res },
        'applications'
      );
      dispatch(SaveApplications(updatedState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  public static async denyApplication(id: number): Promise<any> {
    const { dispatch } = store;
    try {
      const res = await this.apiBase.getAsync(
        `/jobs/applications/${id}/deny/`,
        {}
      );
      const updatedState = prepareUpdatedData(
        'update',
        'applications',
        { id, ...res },
        'applications'
      );
      dispatch(SaveApplications(updatedState));
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  // Display lookup only — do not write into Redux cities (shared with Location filters)
  public static async getLocationById(
    locationId: string | number
  ): Promise<any> {
    try {
      const res = await this.apiBase.getAsync(
        `https://nora.recrout.com/api/cities/${locationId}/`
      );
      return res || null;
    } catch (error) {
      console.error(`Error fetching location with ID ${locationId}:`, error);
      return null;
    }
  }
}
